import React, { Component } from 'react';
import axios from 'axios';
import { NavLink } from 'react-router-dom';
import { hostname } from '../host';
import { getAccessToken } from '../auth/AuthService';
import '../theme/misanthropy.css';

import { AddIcon, ArrowBackIcon, DeleteIcon, EditIcon, RemoveIcon } from './MisIcons';

const EMPTY_FORM = { name: '', gameSystem: '', concept: '', avatarData: '' };

class MisPcRoster extends Component {
  constructor(props) {
    super(props);
    this.state = {
      pcs: [],
      loading: true,
      editOpen: false,
      editId: null,
      form: { ...EMPTY_FORM },
      stats: []
    };
    this.onKey = this.onKey.bind(this);
  }

  componentDidMount() {
    this.load();
    document.addEventListener('keydown', this.onKey);
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.onKey);
  }

  onKey(e) {
    if (e.key === 'Escape' && this.state.editOpen) {
      this.setState({ editOpen: false });
    }
  }

  get headers() { return { Authorization: 'Bearer ' + getAccessToken() }; }

  load() {
    axios.get(hostname() + 'rest/misanthropy/pcs', { headers: this.headers })
      .then(res => this.setState({ pcs: res.data, loading: false }))
      .catch(() => this.setState({ loading: false }));
  }

  openCreate() {
    this.setState({ editOpen: true, editId: null, form: { ...EMPTY_FORM }, stats: [] });
  }

  openEdit(pc) {
    axios.get(hostname() + 'rest/misanthropy/pcs/' + pc.id, { headers: this.headers })
      .then(res => {
        const full = res.data;
        this.setState({
          editOpen: true,
          editId: full.id,
          form: {
            name: full.name || '',
            gameSystem: full.gameSystem || '',
            concept: full.concept || '',
            avatarData: full.avatarData || ''
          },
          stats: (full.stats || []).map(s => ({ statName: s.statName, statValue: s.statValue }))
        });
      }).catch(err => console.error(err));
  }

  save() {
    const { editId, form, stats } = this.state;
    const body = { ...form, stats: stats.filter(s => s.statName.trim()) };
    const req = editId
      ? axios.put(hostname() + 'rest/misanthropy/pcs/' + editId, body, { headers: this.headers })
      : axios.post(hostname() + 'rest/misanthropy/pcs', body, { headers: this.headers });
    req.then(() => {
      this.setState({ editOpen: false });
      this.load();
    }).catch(err => console.error(err));
  }

  delete(id) {
    if (!window.confirm('Delete this character?')) return;
    axios.delete(hostname() + 'rest/misanthropy/pcs/' + id, { headers: this.headers })
      .then(() => this.load())
      .catch(err => console.error(err));
  }

  handleAvatarUpload(e) {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => this.setState(prev => ({ form: { ...prev.form, avatarData: ev.target.result } }));
    reader.readAsDataURL(file);
  }

  setForm(field, value) {
    this.setState(prev => ({ form: { ...prev.form, [field]: value } }));
  }

  addStat() {
    this.setState(prev => ({ stats: [...prev.stats, { statName: '', statValue: '' }] }));
  }

  setStat(index, field, value) {
    this.setState(prev => {
      const stats = [...prev.stats];
      stats[index] = { ...stats[index], [field]: value };
      return { stats };
    });
  }

  removeStat(index) {
    this.setState(prev => ({ stats: prev.stats.filter((_, i) => i !== index) }));
  }

  renderAvatar(src, name, sizeClass, extra) {
    const initial = name ? name.charAt(0) : '?';
    return src
      ? <img className={'mis-avatar ' + sizeClass} src={src} alt={name || 'avatar'} {...extra} />
      : <span className={'mis-avatar ' + sizeClass} {...extra}>{initial}</span>;
  }

  render() {
    const { pcs, loading, editOpen, editId, form, stats } = this.state;

    return (
      <div className="mis-page">
        <div className="mis-inner">
          <div className="mis-topbar">
            <NavLink className="mis-icon-btn" to="/misanthropy" title="Back to sessions">
              <ArrowBackIcon />
            </NavLink>
            <h1 className="mis-h1">Player Characters</h1>
          </div>

          {loading &&
            <div className="mis-loading"><div className="mis-spinner" /></div>}

          {!loading &&
            <div className="mis-grid">
              {pcs.map(pc => (
                <div className="mis-card" key={pc.id}>
                  <div className="mis-card-head">
                    {this.renderAvatar(pc.avatarData, pc.name, 'mis-avatar-48')}
                    <div style={{ minWidth: 0 }}>
                      <h3 className="mis-card-title" title={pc.name}>{pc.name}</h3>
                      {pc.gameSystem && <p className="mis-card-sub">{pc.gameSystem}</p>}
                    </div>
                  </div>
                  <div className="mis-card-body">
                    <p className="mis-card-desc">{pc.concept || 'No concept provided.'}</p>
                  </div>
                  <div className="mis-card-foot">
                    <span className="mis-foot-right">
                      <button className="mis-icon-btn mis-icon-btn-sm" title="Edit"
                        onClick={() => this.openEdit(pc)}><EditIcon /></button>
                      <button className="mis-icon-btn mis-icon-btn-sm mis-danger" title="Delete"
                        onClick={() => this.delete(pc.id)}><DeleteIcon /></button>
                    </span>
                  </div>
                </div>
              ))}

              {pcs.length === 0 &&
                <p className="mis-empty">No characters yet. Press + to create one.</p>}
            </div>}
        </div>

        <button className="mis-fab" title="New character" onClick={() => this.openCreate()}>
          <AddIcon />
        </button>

        {editOpen &&
          <div className="mis-backdrop" role="dialog" aria-modal="true"
            onClick={e => { if (e.currentTarget === e.target) this.setState({ editOpen: false }); }}>
            <div className="mis-modal mis-modal-lg">
              <div className="mis-modal-head">
                <h2 className="mis-modal-title">{editId ? 'Edit Character' : 'New Character'}</h2>
              </div>
              <div className="mis-modal-body">
                <div className="mis-uploader">
                  <div className="mis-uploader-col">
                    {this.renderAvatar(form.avatarData, form.name, 'mis-avatar-80', {
                      onClick: () => document.getElementById('pc-avatar-input').click()
                    })}
                    <input id="pc-avatar-input" type="file" accept="image/*"
                      style={{ display: 'none' }} onChange={e => this.handleAvatarUpload(e)} />
                    <p className="mis-uploader-hint">Click to upload</p>
                  </div>
                  <div className="mis-uploader-fields">
                    <div className="mis-field">
                      <label className="mis-field-label">Name *</label>
                      <input className="mis-input" value={form.name}
                        onChange={e => this.setForm('name', e.target.value)} />
                    </div>
                    <div className="mis-field">
                      <label className="mis-field-label">Game System</label>
                      <input className="mis-input" value={form.gameSystem}
                        onChange={e => this.setForm('gameSystem', e.target.value)} />
                      <p className="mis-help">e.g. "Vampire: the Masquerade", "Call of Cthulhu"</p>
                    </div>
                  </div>
                </div>

                <div className="mis-field">
                  <label className="mis-field-label">Concept</label>
                  <textarea className="mis-textarea" rows={4} value={form.concept}
                    onChange={e => this.setForm('concept', e.target.value)} />
                  <p className="mis-help">Who is this character? The LLM will use this to infer how to play them.</p>
                </div>

                <hr className="mis-divider" />

                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
                  <p className="mis-form-sub" style={{ flex: 1 }}>Stats</p>
                  <button className="mis-btn mis-btn-quiet mis-btn-sm" onClick={() => this.addStat()}>
                    + Add Stat
                  </button>
                </div>

                {stats.map((stat, i) => (
                  <div className="mis-stat-row" key={i}>
                    <input className="mis-input mis-stat-name" placeholder="Stat name" value={stat.statName}
                      onChange={e => this.setStat(i, 'statName', e.target.value)} />
                    <input className="mis-input mis-stat-val" placeholder="Value" value={stat.statValue}
                      onChange={e => this.setStat(i, 'statValue', e.target.value)} />
                    <button className="mis-icon-btn mis-icon-btn-sm" title="Remove stat"
                      onClick={() => this.removeStat(i)}><RemoveIcon /></button>
                  </div>
                ))}

                {stats.length === 0 &&
                  <p className="mis-empty">No stats yet. Add any system-specific stats for dice lookup.</p>}
              </div>
              <div className="mis-modal-foot">
                <button className="mis-btn mis-btn-quiet"
                  onClick={() => this.setState({ editOpen: false })}>Cancel</button>
                <button className="mis-btn mis-btn-primary" disabled={!form.name.trim()}
                  onClick={() => this.save()}>Save</button>
              </div>
            </div>
          </div>}
      </div>
    );
  }
}

export default MisPcRoster;
